import { Schema } from "effect";
import { EmailAddressValue } from "../patient-info/contact-assertion.schema";

/**
 * Insurance categories accepted by kiosk prescription requests.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const insurance: KioskPrescriptionInsuranceType = "PUBLIC";
 * ```
 */
export const KioskPrescriptionInsuranceType = Schema.Literals(
  ["PUBLIC",
  "PRIVATE"],
);

/**
 * Decoded insurance category for a kiosk prescription request.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const insurance: KioskPrescriptionInsuranceType = "PRIVATE";
 * ```
 */
export type KioskPrescriptionInsuranceType =
  typeof KioskPrescriptionInsuranceType.Type;

/**
 * Patient-selected reasons for a kiosk prescription request.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const reason: KioskPrescriptionReason = "chronisch";
 * ```
 */
export const KioskPrescriptionReason = Schema.Literals(
  ["schmerzen",
  "entzuendung",
  "nachsorge",
  "allergie",
  "chronisch",
  "anderes"],
);

/**
 * Decoded patient-selected prescription reason.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const reason: KioskPrescriptionReason = "anderes";
 * ```
 */
export type KioskPrescriptionReason = typeof KioskPrescriptionReason.Type;

/**
 * Patient-selected urgency values for a kiosk prescription request.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const urgency: KioskPrescriptionUrgency = "week";
 * ```
 */
export const KioskPrescriptionUrgency = Schema.Literals(
  ["soon",
  "tomorrow",
  "week"],
);

/**
 * Decoded patient-selected prescription urgency.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const urgency: KioskPrescriptionUrgency = "soon";
 * ```
 */
export type KioskPrescriptionUrgency = typeof KioskPrescriptionUrgency.Type;

const NonEmptyTrimmedString = Schema.Trim.pipe(Schema.check(Schema.isMinLength(1)));

/**
 * Successful eGK identification with its required card-read proof.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const identification: KioskPrescriptionIdentification = {
 *   kind: "egk",
 *   crlogId: "card-read-1",
 * };
 * ```
 */
export const KioskPrescriptionEgkIdentification = Schema.Struct({
  kind: Schema.Literal("egk"),
  crlogId: NonEmptyTrimmedString,
});

/**
 * Explicit manual identification without forged eGK proof.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const identification: KioskPrescriptionIdentification = {
 *   kind: "manual",
 * };
 * ```
 */
export const KioskPrescriptionManualIdentification = Schema.Struct({
  kind: Schema.Literal("manual"),
  crlogId: Schema.optional(Schema.Never),
});

/**
 * Discriminated provenance for a kiosk prescription request.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const identification: KioskPrescriptionIdentification = {
 *   kind: "manual",
 * };
 * ```
 */
export const KioskPrescriptionIdentification = Schema.Union(
  [KioskPrescriptionEgkIdentification,
  KioskPrescriptionManualIdentification],
);

/**
 * Decoded prescription-request identification provenance.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const identification: KioskPrescriptionIdentification = {
 *   kind: "egk",
 *   crlogId: "card-read-1",
 * };
 * ```
 */
export type KioskPrescriptionIdentification =
  typeof KioskPrescriptionIdentification.Type;

/**
 * Client-safe payload for a policy-governed kiosk prescription request.
 *
 * The Bridge resolves the patient name from the existing patient record; the
 * kiosk never supplies staff-facing identity text.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const request = new KioskCreatePrescriptionRequest({
 *   patientId: "patient-1",
 *   insuranceType: "PUBLIC",
 *   identification: { kind: "manual" },
 *   requestText: "Bitte mein Dauermedikament erneut verordnen.",
 *   quarterlyWarning: false,
 *   reason: "chronisch",
 *   urgency: "week",
 * });
 * ```
 */
export class KioskCreatePrescriptionRequest extends Schema.Class<KioskCreatePrescriptionRequest>(
  "KioskCreatePrescriptionRequest",
)({
  patientId: NonEmptyTrimmedString,
  insuranceType: KioskPrescriptionInsuranceType,
  identification: KioskPrescriptionIdentification,
  requestText: NonEmptyTrimmedString,
  quarterlyWarning: Schema.Boolean,
  reason: Schema.optional(KioskPrescriptionReason),
  urgency: Schema.optional(KioskPrescriptionUrgency),
  completionEmail: Schema.optional(EmailAddressValue),
}) {}

/**
 * Successful kiosk prescription-request response.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const response: KioskCreatePrescriptionResponse = { taskId: "task-1" };
 * ```
 */
export const KioskCreatePrescriptionResponse = Schema.Struct({
  taskId: Schema.String,
});

/**
 * Decoded successful kiosk prescription-request response.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const response: KioskCreatePrescriptionResponse = { taskId: "task-1" };
 * ```
 */
export type KioskCreatePrescriptionResponse =
  typeof KioskCreatePrescriptionResponse.Type;

/**
 * Coarse reasons a kiosk prescription request did not complete.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const reason: KioskPrescriptionRequestRejectionReason = "policy-disabled";
 * ```
 */
export const KioskPrescriptionRequestRejectionReason = Schema.Literals(
  ["policy-disabled",
  "policy-unavailable",
  "patient-not-found",
  "creation-failed"],
);

/**
 * Decoded kiosk prescription-request rejection reason.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const reason: KioskPrescriptionRequestRejectionReason = "creation-failed";
 * ```
 */
export type KioskPrescriptionRequestRejectionReason =
  typeof KioskPrescriptionRequestRejectionReason.Type;

/**
 * Typed, non-sensitive rejection returned by kiosk prescription submission.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const error: KioskPrescriptionRequestRejected = {
 *   _tag: "KioskPrescriptionRequestRejected",
 *   reason: "policy-unavailable",
 * };
 * ```
 */
export const KioskPrescriptionRequestRejected = Schema.TaggedStruct(
  "KioskPrescriptionRequestRejected",
  {
    reason: KioskPrescriptionRequestRejectionReason,
  },
);

/**
 * Decoded typed rejection returned by kiosk prescription submission.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const error: KioskPrescriptionRequestRejected = {
 *   _tag: "KioskPrescriptionRequestRejected",
 *   reason: "patient-not-found",
 * };
 * ```
 */
export type KioskPrescriptionRequestRejected =
  typeof KioskPrescriptionRequestRejected.Type;
